import React, { useEffect, useState } from 'react';
import { useRef } from 'react';
import ReactDOM from 'react-dom';
import { Alert, Button } from 'react-bootstrap';
import { BsReply, BsFileEarmarkText } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import convStyles from './openTickets/Conversations.module.css';
import { baseURL } from '../../../helpers/baseURL';
import { headers } from '../../../helpers/Requests';
import { getUserProfileImage, createMarkup } from '../../../helpers/generic';
import { splitClaritaskCommentFiles, emphasizeClaritaskNoteActor } from '../../../helpers/claritaskCommentFiles';
import ActivityFeed from './ActivityFeed';
import ConversationAttachments from './openTickets/ConversationAttachments';
import CustomEditor from '../../Editor';
import QuickRepliesPicker from '../../QuickRepliesPicker';
import {
    createEmptyQuickReplyComposeState,
    formatUnresolvedQuickReplyMessage,
    prepareQuickReplyComposeContent,
} from '../../../helpers/quickReplyCompose';
import { formatDateTime, formatDateOnly as formatDueDate } from '../../../helpers/formatDate';

// ─── Shared sub-components ────────────────────────────────────────────────────

const LoadingRow = () => (
	<div style={{ padding: '30px 0', textAlign: 'center', color: '#999', fontSize: 13 }}>Loading…</div>
);

const EmptyRow = ({ msg = 'No data found.' }) => (
	<div style={{ padding: '30px 0', textAlign: 'center', color: '#bbb', fontSize: 13 }}>{msg}</div>
);

const DetailRow = ({ label, value }) => (
	<div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', padding: '8px 8px 8px 0', fontSize: 13, textAlign: 'left' }}>
		<span style={{ width: 110, color: '#888', flexShrink: 0, textAlign: 'left' }}>{label}</span>
		<span style={{ color: '#1a1a2e', fontWeight: 500, textAlign: 'left', wordBreak: 'break-word', minWidth: 0 }}>{value || '—'}</span>
	</div>
);

// ─── Tab: Details ─────────────────────────────────────────────────────────────

const DetailsTab = ({ details, loading, users }) => {
	if (loading) return <LoadingRow />;
	if (!details) return <EmptyRow />;

	const assigneeUsers = (details.assignees || []).map(a => {
		if (a && typeof a === 'object' && a.name) return a;
		return (users || []).find(u => u.id === a) || { name: `#${a}` };
	});

	const tags = (details.tags || []).map(t => t.name).join(', ');

	return (
		<div>
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
				<DetailRow label="Title"      value={details.title} />
				<DetailRow label="Status"     value={details.status?.name} />
				<DetailRow label="Department" value={details.department?.name} />
				<DetailRow label="Priority"   value={details.priority?.name} />
				<DetailRow label="Type"       value={details.type?.name} />
				<DetailRow label="Assignees"  value={
					assigneeUsers.length > 0
						? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
							{assigneeUsers.map((u, i) => (
								<span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
									<img
										src={u.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name)}`}
										style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }}
									/>
									<span style={{ fontSize: 13, color: '#1a1a2e' }}>{u.name}</span>
								</span>
							))}
						</div>
						: '—'
				} />
				<DetailRow label="Created By" value={details.created_by?.name} />
				<DetailRow label="Tags"       value={tags || '—'} />
				<DetailRow label="Replies"    value={details.replies_count ?? '—'} />
				<DetailRow label="Due Date"   value={formatDueDate(details.due_date)} />
				<DetailRow label="Replied At" value={details.replied_at ? formatDateTime(details.replied_at) : '—'} />
				<DetailRow label="Created At" value={details.created_at ? formatDateTime(details.created_at) : '—'} />
				<DetailRow label="Updated At" value={details.updated_at ? formatDateTime(details.updated_at) : '—'} />
				<DetailRow label="Closed At"  value={details.closed_at  ? formatDateTime(details.closed_at)  : '—'} />
			</div>
			<div style={{ padding: '8px 8px 8px 0', fontSize: 13, textAlign: 'left' }}>
				<span style={{ color: '#888' }}>Description</span>
				<div
					style={{ color: '#1a1a2e', fontWeight: 500, marginTop: 4, lineHeight: 1.5 }}
					dangerouslySetInnerHTML={createMarkup(details.description || '—')}
				/>
			</div>
		</div>
	);
};

// ─── Tab: History ─────────────────────────────────────────────────────────────
// Replaced by ActivityFeed — see below in the render.

// ─── ComposeBox ───────────────────────────────────────────────────────────────

const ComposeBox = ({ ticketId, ticket, type, onCommentAdded }) => {
	const editorRef = useRef(null);
	const quickReplyComposeRef = useRef(createEmptyQuickReplyComposeState());
	const user = useSelector((state) => state.userReducer?.user);
	const [status, setStatus] = useState(null);
	const [message, setMessage] = useState('');
	const [isInvalid, setIsInvalid] = useState(false);
	const [editorResetKey, setEditorResetKey] = useState(0);

	useEffect(() => {
		quickReplyComposeRef.current = createEmptyQuickReplyComposeState();
	}, [editorResetKey]);

	const handleEditorChange = () => {
		if (quickReplyComposeRef.current?.draft) {
			quickReplyComposeRef.current = {
				...quickReplyComposeRef.current,
				modified: true,
			};
		}
	};

	const handleSubmit = async () => {
		if (!editorRef.current || editorRef.current.getText().trim().length === 0) {
			setIsInvalid(true);
			setStatus('error');
			setMessage('Please enter a message!');
			return;
		}

		const { content, unresolved } = prepareQuickReplyComposeContent({
			editorHtml: editorRef.current.getHTML(),
			quickReplyCompose: quickReplyComposeRef.current,
			ticket,
			user,
		});

		if (unresolved.length > 0) {
			setIsInvalid(true);
			setStatus('error');
			setMessage(formatUnresolvedQuickReplyMessage(unresolved));
			return;
		}

		setIsInvalid(false);
		setStatus('loading');
		try {
			const res = await axios.post(`${baseURL}comments/${ticketId}`, { type, content, attachments: [] }, headers);
			editorRef.current.reset();
			setEditorResetKey((key) => key + 1);
			quickReplyComposeRef.current = createEmptyQuickReplyComposeState();
			setStatus('success');
			setMessage(type === 'note' ? 'Note saved!' : 'Reply posted!');
			if (onCommentAdded && res.data?.comment) {
				onCommentAdded(res.data.comment);
			}
			setTimeout(() => setStatus(null), 3000);
		} catch (e) {
			setStatus('error');
			setMessage(e?.response?.data?.message || 'Something went wrong!');
		}
	};

	return (
		<div style={{ marginTop: 8 }}>
			{status === 'loading' && <Alert variant="info">Loading...</Alert>}
			{status === 'success' && <Alert variant="success">{message}</Alert>}
			{status === 'error'   && <Alert variant="danger">{message}</Alert>}
			<div style={{ textAlign: 'left' }}>
				<CustomEditor
					ref={editorRef}
					id={`compose-${type}-${ticketId}`}
					placeholder={type === 'note' ? 'Please add your note here' : 'Please add your reply here'}
					isInvalid={isInvalid}
					onChange={handleEditorChange}
				/>
			</div>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
				<QuickRepliesPicker
					editorRef={editorRef}
					ticket={ticket}
					disabled={status === 'loading'}
					editorResetKey={editorResetKey}
					composeRef={quickReplyComposeRef}
				/>
				<Button onClick={handleSubmit} disabled={status === 'loading'}>
					{type === 'reply' && <><BsReply className="me-1 mb-1" size={15} /> Post Reply</>}
					{type === 'note'  && <><BsFileEarmarkText className="me-1 mb-1" size={15} /> Save Note</>}
				</Button>
			</div>
		</div>
	);
};

// ─── Tab: Conversations ───────────────────────────────────────────────────────

const ConvTab = ({ details, loading, ticketId, composableTabMessages, composableTabCompose }) => {
	const [replies, setReplies] = useState(null);

	useEffect(() => {
		if (details?.replies) setReplies(details.replies.filter(r => r.type === 'reply'));
	}, [details]);

	const items = replies ?? (details?.replies || []).filter(r => r.type === 'reply');

	if (loading) return <div style={composableTabMessages}><LoadingRow /></div>;
	return (
		<div style={composableTabLayout}>
			<div style={composableTabMessages} className={convStyles.container} data-type="replies">
				{items.length === 0
					? <EmptyRow msg="No conversations found." />
					: items.map(r => (
						<div key={r.id} className={convStyles.boxContainer} data-role={r.role === 'customer' ? 'customer' : 'agent'} data-type="reply">
							<div className={convStyles.header}>
								<div className={convStyles.user}>
									<img src={getUserProfileImage(r.user)} alt="profile" style={{ width: 50, height: 50, borderRadius: '50%' }} />
									<div>{r.user?.name}</div>
								</div>
								<div className={convStyles.wrapper}>
									<div className={convStyles.contentContainer} dangerouslySetInnerHTML={createMarkup(r.content)} />
									<ConversationAttachments attachments={r.attachments || []} />
									<div className={convStyles.date}>{formatDateTime(r.created_at)}</div>
								</div>
							</div>
						</div>
					))
				}
			</div>
			<div style={composableTabCompose}>
				<ComposeBox ticketId={ticketId} ticket={details} type="reply" onCommentAdded={(comment) => setReplies(prev => [comment, ...(prev ?? [])])} />
			</div>
		</div>
	);
};

// ─── Tab: Notes ───────────────────────────────────────────────────────────────

const NotesTab = ({ details, notes, loading, ticketId, composableTabMessages, composableTabCompose }) => {
	const [items, setItems] = useState(null);

	useEffect(() => {
		const src = details?.notes?.length ? details.notes : notes;
		if (src?.length) setItems(src);
	}, [details, notes]);

	const list = items ?? (details?.notes?.length ? details.notes : notes);

	if (loading) return <div style={composableTabMessages}><LoadingRow /></div>;
	return (
		<div style={composableTabLayout}>
			<div style={composableTabMessages} className={convStyles.container} data-type="notes">
				{!list.length
					? <EmptyRow msg="No notes found." />
					: list.map(n => (
						<div key={n.id} className={convStyles.boxContainer} data-role={n.role === 'customer' ? 'customer' : 'agent'} data-type="note">
							<div className={convStyles.header}>
								<div className={convStyles.user}>
									<img src={getUserProfileImage(n.user)} alt="profile" style={{ width: 50, height: 50, borderRadius: '50%' }} />
									<div>{n.user?.name}</div>
								</div>
								<div className={convStyles.wrapper}>
									<div className={convStyles.contentContainer} dangerouslySetInnerHTML={createMarkup(emphasizeClaritaskNoteActor(n.content || ''))} />
									<ConversationAttachments attachments={n.attachments || []} />
									<div className={convStyles.date}>{formatDateTime(n.created_at)}</div>
								</div>
							</div>
						</div>
					))
				}
			</div>
			<div style={composableTabCompose}>
				<ComposeBox ticketId={ticketId} ticket={details} type="note" onCommentAdded={(comment) => setItems(prev => [comment, ...(prev ?? [])])} />
			</div>
		</div>
	);
};

// ─── Tab: Claritask Comments ──────────────────────────────────────────────────

const ClaritaskCommentsTab = ({ claritaskComments, loading, scrollRef }) => {
	const claritask = claritaskComments;

	useEffect(() => {
		if (loading || !claritask.length) return;
		const el = scrollRef?.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [loading, claritask.length, claritask[claritask.length - 1]?.id, scrollRef]);

	if (loading) return <LoadingRow />;
	if (!claritask.length) return <EmptyRow msg="No Claritask comments found." />;
	const firstComment = claritask[0];
	return (
		<div style={{ position: 'relative', paddingTop: firstComment.task_id ? 40 : 0 }}>
			{firstComment.task_id && (
				<div style={{ position: 'absolute', top: 0, right: 0 }}>
					<a
						className="btn btn-primary"
						href={`http://claritask.com/a/workspace/${firstComment.workspace_id}/project/${firstComment.project_id}/board/${firstComment.board_id}/${firstComment.route_type}?target=${firstComment.task_id}`}
						target="_blank"
						rel="noreferrer"
						style={{ fontSize: 12 }}
					>
						View Task in Claritask
					</a>
				</div>
			)}
			<div className={convStyles.container} data-type="claritask">
				{claritask.map(c => {
					const { content, attachments } = splitClaritaskCommentFiles(c);
					return (
					<div key={c.id} className={convStyles.boxContainer} data-role="agent" data-type="claritask">
						<div className={convStyles.header}>
							<div className={convStyles.user}>
								<img src={getUserProfileImage(c.user)} alt="profile" style={{ width: 50, height: 50, borderRadius: '50%' }} />
								<div>{c.user?.name}</div>
							</div>
							<div className={convStyles.wrapper}>
								<div className={convStyles.contentContainer} dangerouslySetInnerHTML={createMarkup(content)} />
								<ConversationAttachments attachments={attachments} />
								<div className={convStyles.date}>{formatDateTime(c.created_at)}</div>
							</div>
						</div>
					</div>
					);
				})}
			</div>
		</div>
	);
};

// ─── Inline styles ────────────────────────────────────────────────────────────

const overlay = {
	position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
	display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modal = {
	background: '#fff', border: '1px solid #e0e0e0', borderRadius: 5,
	width: '720px', maxWidth: '95vw', height: '80vh', maxHeight: '85vh',
	display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const modalHeader = {
	display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
	padding: '14px 20px', borderBottom: '1px solid #ebebeb',
};
const tabBar = {
	display: 'flex', borderBottom: '1px solid #ebebeb',
	padding: '0 20px', gap: 2, background: '#fafafa',
	overflowX: 'auto', flexWrap: 'nowrap',
};
const tabBtn = {
	background: 'none', border: 'none', borderBottom: '2px solid transparent',
	padding: '9px 14px', fontSize: 13, color: '#666', cursor: 'pointer', outline: 'none',
	whiteSpace: 'nowrap', flexShrink: 0,
};
const tabBtnActive = {
	borderBottom: '2px solid #18AEE3', color: '#18AEE3', fontWeight: 600,
};
const tabContent = {
	flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
};
// For tabs that only scroll (Details, History, Claritask Comments)
const scrollableTab = {
	flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 20px',
};
// For tabs with a pinned ComposeBox (Conversations, Notes)
const composableTabMessages = {
	flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 20px 0 20px',
};
const composableTabLayout = {
	flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
};
const composableTabCompose = {
	flexShrink: 0, padding: '0 20px 12px 20px', borderTop: '1px solid #e0e0e0', background: '#fff',
};
const modalFooter = {
	padding: '12px 20px', borderTop: '1px solid #ebebeb', textAlign: 'right',
};
const closeBtnStyle = {
	background: 'none', border: 'none', fontSize: 16,
	cursor: 'pointer', color: '#888', padding: '2px 6px', lineHeight: 1,
};
const cancelBtn = {
	padding: '6px 20px', fontSize: 13, cursor: 'pointer',
	border: '1px solid #18AEE3', borderRadius: 5,
	background: '#fff', color: '#18AEE3', fontWeight: 500,
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ActivityModal = ({ ticketId, onClose, users }) => {
	const [activeTab, setActiveTab]   = useState('details');
	const [details, setDetails]       = useState(null);
	const [notes, setNotes]           = useState([]);
	const [claritaskComments, setClaritaskComments] = useState([]);
	const [loading, setLoading]       = useState({ details: true, notes: false, claritaskComments: false });
	const historyContainerRef         = useRef(null);
	const claritaskScrollRef          = useRef(null);

	// Lock body scroll while modal is open
	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => { document.body.style.overflow = ''; };
	}, []);

	// Prevent state updates on unmounted component
	const isMounted = useRef(true);
	useEffect(() => {
		isMounted.current = true;
		return () => { isMounted.current = false; };
	}, []);

	useEffect(() => { fetchDetails(); }, [ticketId]);

	useEffect(() => {
		if (activeTab === 'notes'     && notes.length === 0)             fetchNotes();
		if (activeTab === 'claritask' && claritaskComments.length === 0) fetchClaritaskComments();
	}, [activeTab]);

	const fetchDetails = async () => {
		setLoading(p => ({ ...p, details: true }));
		try {
			const res = await axios.get(`${baseURL}ticket/${ticketId}`, headers);
			if (!isMounted.current) return;
			setDetails(res.data.data || res.data.ticket || res.data);
		} catch (e) { console.error(e); }
		finally { if (isMounted.current) setLoading(p => ({ ...p, details: false })); }
	};

	const fetchNotes = async () => {
		setLoading(p => ({ ...p, notes: true }));
		try {
			const res = await axios.get(`${baseURL}ticket/${ticketId}`, headers);
			if (!isMounted.current) return;
			const ticket = res.data.data || res.data.ticket || res.data;
			setNotes(ticket.notes || []);
		} catch (e) { console.error(e); }
		finally { if (isMounted.current) setLoading(p => ({ ...p, notes: false })); }
	};

	const fetchClaritaskComments = async () => {
		setLoading(p => ({ ...p, claritaskComments: true }));
		try {
			const res = await axios.get(`${baseURL}tickets/${ticketId}/claritask-comments`, headers);
			if (!isMounted.current) return;
			setClaritaskComments(res.data.claritask_comments || []);
		} catch (e) { console.error(e); }
		finally { if (isMounted.current) setLoading(p => ({ ...p, claritaskComments: false })); }
	};

	const tabs = [
		{ key: 'details',   label: 'Details'           },
		{ key: 'history',   label: 'History'            },
		{ key: 'conv',      label: 'Conversations'      },
		{ key: 'notes',     label: 'Notes'              },
		{ key: 'claritask', label: 'Claritask Comments' },
	];

	return ReactDOM.createPortal(
		<div style={overlay} onClick={onClose}>
			<div style={modal} onClick={e => e.stopPropagation()}>

				{/* Header */}
				<div style={modalHeader}>
					<span style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e', wordBreak: 'break-word', paddingRight: 12 }}>
						{details?.title && <span>{details.title}</span>}
					</span>
					<button onClick={onClose} style={closeBtnStyle}>✕</button>
				</div>

				{/* Tabs */}
				<div style={tabBar}>
					{tabs.map(t => (
						<button
							key={t.key}
							onClick={() => setActiveTab(t.key)}
							style={{ ...tabBtn, ...(activeTab === t.key ? tabBtnActive : {}) }}
						>
							{t.label}
						</button>
					))}
				</div>

				{/* Content */}
				<div style={tabContent}>
					{activeTab === 'details'   && <div style={scrollableTab}><DetailsTab details={details} loading={loading.details} users={users} /></div>}
					{activeTab === 'history'   && <div ref={historyContainerRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}><ActivityFeed ticketId={ticketId} containerRef={historyContainerRef} /></div>}
					{activeTab === 'conv'      && <ConvTab      details={details} loading={loading.details}   ticketId={ticketId} composableTabMessages={composableTabMessages} composableTabCompose={composableTabCompose} />}
					{activeTab === 'notes'     && <NotesTab     details={details} notes={notes} loading={loading.notes} ticketId={ticketId} composableTabMessages={composableTabMessages} composableTabCompose={composableTabCompose} />}
					{activeTab === 'claritask' && <div ref={claritaskScrollRef} style={scrollableTab}><ClaritaskCommentsTab claritaskComments={claritaskComments} loading={loading.claritaskComments} scrollRef={claritaskScrollRef} /></div>}
				</div>

				{/* Footer */}
				<div style={modalFooter}>
					<button onClick={onClose} style={cancelBtn}>Close</button>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default ActivityModal;
